import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { chatReducer } from './chatReducer';
import {
  initialChatState,
  ChatState,
  ChatAction,
  ConversationData,
  ChatMessage,
} from './chatTypes';
import { useAuth } from '../../web3/context/AuthContext';
import { createSigner, getChatAddress } from '../signers/signerFactory';
import { Client } from '@xmtp/browser-sdk';
import {
  initializeXmtpClient,
  buildXmtpClient,
  createXmtpClient,
  closeXmtpClient,
  acquireTabLock,
  releaseTabLock,
  canMessage,
  InstallationLimitError,
  registerAfterRevocation,
} from '../services/xmtpClient';
import { backupXmtpDatabase, ensureFirebaseBackup } from '../services/xmtpBackup';
import type { RemoteAttachmentMetadata } from '../services/attachmentService';
import toast from 'react-hot-toast';

const XMTP_ENV = process.env.REACT_APP_NETWORK_TYPE === 'TESTNET' ? 'dev' : 'production';

const ATTACHMENT_PREFIX = '__horus_attachment__';

// ---- Context interfaces ----

interface ChatClientContextValue {
  isInitialized: boolean;
  isInitializing: boolean;
  initError: string | null;
  initProgress: string | null;
  chatAddress: string | null;
  initializeChat: (password?: string) => Promise<void>;
  showInstallationManager: boolean;
  pendingClient: any;
  pendingInboxId: string | null;
  completeRegistration: () => Promise<void>;
  dismissInstallationManager: () => void;
  revokeInstallations: (installationBytes: Uint8Array[], revokeAll: boolean) => Promise<void>;
}

interface ChatDataContextValue {
  conversations: ConversationData[];
  activeConversationId: string | null;
  messages: Record<string, ChatMessage[]>;
  loadingMessages: Record<string, boolean>;
  hasMoreMessages: Record<string, boolean>;
  unreadCounts: Record<string, number>;
  totalUnread: number;
  searchQuery: string;
  isSyncing: boolean;
  conversationFilter: 'all' | 'dms' | 'groups';
  dispatch: React.Dispatch<ChatAction>;
  lastReadAt: number; // timestamp snapshot from before conversation was marked as read
  sendMessage: (conversationId: string, text: string, attachment?: RemoteAttachmentMetadata) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  loadOlderMessages: (conversationId: string) => Promise<void>;
  selectConversation: (conversationId: string | null) => void;
  createDm: (peerAddress: string) => Promise<string | null>;
  createGroup: (memberAddresses: string[], name?: string) => Promise<string | null>;
  addGroupMembers: (conversationId: string, addresses: string[]) => Promise<void>;
  removeGroupMember: (conversationId: string, address: string) => Promise<void>;
  syncConversations: () => Promise<void>;
}

const ChatClientContext = createContext<ChatClientContextValue>({
  isInitialized: false,
  isInitializing: false,
  initError: null,
  initProgress: null,
  chatAddress: null,
  initializeChat: async () => {},
  showInstallationManager: false,
  pendingClient: null,
  pendingInboxId: null,
  completeRegistration: async () => {},
  dismissInstallationManager: () => {},
  revokeInstallations: async () => {},
});

const ChatDataContext = createContext<ChatDataContextValue>({
  conversations: [],
  activeConversationId: null,
  messages: {},
  loadingMessages: {},
  hasMoreMessages: {},
  unreadCounts: {},
  totalUnread: 0,
  searchQuery: '',
  isSyncing: false,
  conversationFilter: 'all',
  lastReadAt: 0,
  dispatch: () => {},
  sendMessage: async () => {},
  loadMessages: async () => {},
  loadOlderMessages: async () => {},
  selectConversation: () => {},
  createDm: async () => null,
  createGroup: async () => null,
  addGroupMembers: async () => {},
  removeGroupMember: async () => {},
  syncConversations: async () => {},
});

// ---- Last-read tracking for unread counts ----

const LAST_READ_PREFIX = 'horus-chat-lastread-';

function getLastReadTime(conversationId: string): number {
  const raw = localStorage.getItem(LAST_READ_PREFIX + conversationId);
  return raw ? parseInt(raw, 10) : 0;
}

function setLastReadTime(conversationId: string): void {
  localStorage.setItem(LAST_READ_PREFIX + conversationId, Date.now().toString());
}

// ---- Local cache for instant sidebar + message loading ----

const CONVO_CACHE_PREFIX = 'horus-chat-convos-';
const MSG_CACHE_PREFIX = 'horus-chat-msgs-';
const MSG_CACHE_LIMIT = 30; // max messages cached per conversation

function getCachedConversations(address: string): ConversationData[] | null {
  try {
    const raw = localStorage.getItem(CONVO_CACHE_PREFIX + address.toLowerCase());
    if (!raw) return null;
    const arr = JSON.parse(raw);
    // Revive Date fields
    return arr.map((c: any) => ({
      ...c,
      createdAt: new Date(c.createdAt),
      lastMessageTime: c.lastMessageTime ? new Date(c.lastMessageTime) : undefined,
    }));
  } catch { return null; }
}

function setCachedConversations(address: string, conversations: ConversationData[]): void {
  try {
    localStorage.setItem(CONVO_CACHE_PREFIX + address.toLowerCase(), JSON.stringify(conversations));
  } catch (err) {
    console.warn('[Cache] Failed to save conversations:', err);
  }
}

function getCachedMessages(conversationId: string): ChatMessage[] | null {
  try {
    const key = MSG_CACHE_PREFIX + conversationId;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return arr.map((m: any) => ({ ...m, sentAt: new Date(m.sentAt) }));
  } catch (err) {
    console.warn('[Cache] Failed to read messages for', conversationId, err);
    return null;
  }
}

function setCachedMessages(conversationId: string, messages: ChatMessage[]): void {
  try {
    // Only cache the most recent messages to avoid localStorage bloat
    const toCache = messages.slice(-MSG_CACHE_LIMIT);
    const key = MSG_CACHE_PREFIX + conversationId;
    localStorage.setItem(key, JSON.stringify(toCache));
  } catch (err) {
    console.warn('[Cache] Failed to save messages for', conversationId, err);
  }
}

/**
 * Merge two message arrays, deduplicating by ID and sorting by time.
 * Preserves messages from both sources (cache + network).
 */
function mergeMessages(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const map = new Map<string, ChatMessage>();
  // Add a first, then b — b wins on duplicates (newer/resolved data)
  for (const m of a) map.set(m.id, m);
  for (const m of b) map.set(m.id, m);
  return Array.from(map.values()).sort(
    (x, y) => x.sentAt.getTime() - y.sentAt.getTime()
  );
}

/**
 * Update a single conversation's last-message preview in the localStorage cache.
 * Called after sending a message or receiving one via stream, so the sidebar
 * preview survives a page refresh without waiting for syncConversations.
 */
function updateConversationCachePreview(
  address: string,
  conversationId: string,
  text: string,
  time: Date,
  sender: string,
): void {
  const cached = getCachedConversations(address);
  if (!cached) return;
  const idx = cached.findIndex((c) => c.id === conversationId);
  if (idx === -1) return;
  cached[idx].lastMessageText = text;
  cached[idx].lastMessageTime = time;
  cached[idx].lastMessageSender = sender;
  // Re-sort by latest message time
  cached.sort((a, b) => {
    const tA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
    const tB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
    return tB - tA;
  });
  setCachedConversations(address, cached);
}

// ---- Helper to convert XMTP messages to ChatMessage ----

// XMTP internal content types that should not be rendered as user messages
const SYSTEM_CONTENT_TYPES = new Set([
  'group_membership_change',
  'group_updated',
]);

function isSystemContent(content: any): boolean {
  if (typeof content !== 'object' || content === null) return false;
  // XMTP group membership change messages have this shape
  return 'initiatedByInboxId' in content && 'addedInboxes' in content && 'removedInboxes' in content;
}

/**
 * Parse a timestamp from XMTP objects.
 * The browser-sdk uses nanosecond BigInt (sentAtNs, createdAtNs) as primary timestamps.
 * Some SDK versions also expose Date getters (sentAt, createdAt).
 */
function parseXmtpTimestamp(nsValue: any, dateValue: any): Date {
  // Prefer nanosecond BigInt — always present in browser-sdk
  if (nsValue != null) {
    try {
      return new Date(Number(BigInt(nsValue) / BigInt(1_000_000)));
    } catch {
      // Fallback if BigInt conversion fails
    }
  }
  // Date object (getter in some SDK versions)
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return dateValue;
  }
  // Millisecond number
  if (typeof dateValue === 'number' && dateValue > 0) {
    return new Date(dateValue);
  }
  return new Date();
}

function toMessage(msg: any, myInboxId: string): ChatMessage | null {
  const contentTypeId = msg.contentType?.typeId || '';

  // Skip XMTP internal/system messages
  if (SYSTEM_CONTENT_TYPES.has(contentTypeId) || isSystemContent(msg.content)) {
    return null;
  }

  const senderInboxId = msg.senderInboxId || '';
  let content = msg.content;
  let contentType = contentTypeId || 'text';

  // Detect Horus attachment messages (JSON with prefix)
  if (typeof content === 'string' && content.startsWith(ATTACHMENT_PREFIX)) {
    try {
      content = JSON.parse(content.slice(ATTACHMENT_PREFIX.length));
      contentType = 'remoteAttachment';
    } catch {
      // Not valid JSON, treat as text
    }
  }

  // Skip messages with no displayable content
  if (content === undefined || content === null || content === '') {
    return null;
  }

  const sentAt = parseXmtpTimestamp(msg.sentAtNs, msg.sentAt);

  return {
    id: msg.id || `${sentAt.getTime()}-${senderInboxId}`,
    conversationId: msg.conversationId || '',
    senderAddress: senderInboxId,
    content,
    contentType,
    sentAt,
    // Compare inbox IDs — senderInboxId and client.inboxId are both XMTP inbox IDs
    isSentByMe: senderInboxId.toLowerCase() === myInboxId.toLowerCase(),
  };
}

/**
 * Resolve an XMTP inbox ID to an Ethereum address via the client.
 */
async function resolveInboxIdToAddress(client: any, inboxId: string): Promise<string> {
  if (!inboxId) return '';
  try {
    // Must use client.preferences — browser-sdk Client doesn't expose getLatestInboxState directly
    const state = await client.preferences.getLatestInboxState(inboxId);
    const ethId = state?.identifiers?.find(
      (id: any) => id.identifierKind === 'Ethereum'
    );
    return ethId?.identifier || inboxId;
  } catch {
    return inboxId; // Fallback to inbox ID if resolution fails
  }
}

async function toConversation(convo: any, client: any): Promise<ConversationData> {
  // Fetch members — in the browser-sdk, members() is an async method
  let rawMembers: any[] = [];
  try {
    rawMembers = typeof convo.members === 'function'
      ? await convo.members()
      : Array.isArray(convo.members) ? convo.members : [];
  } catch {
    // Non-critical
  }

  const isGroup = rawMembers.length > 2 || !!convo.name;

  // Resolve member inbox IDs to Ethereum addresses
  let memberAddresses: string[] = [];
  if (isGroup && rawMembers.length > 0) {
    memberAddresses = await Promise.all(
      rawMembers.map(async (m: any) => {
        const inboxId = m.inboxId || m;
        return resolveInboxIdToAddress(client, inboxId);
      })
    );
  }

  // peerInboxId is an async method in the XMTP SDK, not a property
  let peerInboxId = '';
  if (!isGroup && typeof convo.peerInboxId === 'function') {
    try {
      peerInboxId = await convo.peerInboxId();
    } catch {
      peerInboxId = '';
    }
  }
  // Resolve inbox ID to Ethereum address for display
  const peerAddress = peerInboxId
    ? await resolveInboxIdToAddress(client, peerInboxId)
    : '';

  return {
    id: convo.id || convo.topic,
    topic: convo.topic || convo.id,
    peerAddress,
    isGroup,
    groupName: convo.name || undefined,
    members: memberAddresses,
    createdAt: parseXmtpTimestamp(convo.createdAtNs, convo.createdAt),
    lastMessageText: undefined,
    lastMessageTime: undefined,
  };
}

// ---- Provider ----

const MESSAGE_PAGE_SIZE = 30;

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const { isLoggedIn, loginOption, entityInfo, email, hasPassword, userWalletsData } = useAuth();
  const streamRef = useRef<any>(null);
  const convoStreamRef = useRef<any>(null);
  const lastReadAtRef = useRef<number>(0);
  const pendingSignerRef = useRef<any>(null);
  const clientRef = useRef(state.client);
  const activeConvoRef = useRef<string | null>(state.activeConversationId);
  const autoInitAttemptedRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    clientRef.current = state.client;
  }, [state.client]);

  useEffect(() => {
    activeConvoRef.current = state.activeConversationId;
  }, [state.activeConversationId]);

  const chatAddress = getChatAddress({
    loginOption,
    activePublicKey: entityInfo.activePublicKey,
    email,
    indexWallet: entityInfo.indexWallet,
    hasPassword,
    userWalletsData: userWalletsData as any,
  });

  // ---- Initialize XMTP ----
  const initializeChat = useCallback(
    async (password?: string) => {
      if (state.isInitialized || state.isInitializing) return;

      // Tab lock check
      if (!acquireTabLock()) {
        toast.error('Chat is already open in another tab. Please close it first.');
        dispatch({ type: 'SET_INIT_ERROR', payload: 'Chat is active in another tab' });
        return;
      }

      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_ERROR', payload: null });
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Preparing chat...' });

      try {
        const authInfo = {
          loginOption,
          activePublicKey: entityInfo.activePublicKey,
          email,
          indexWallet: entityInfo.indexWallet,
          hasPassword,
          userWalletsData: userWalletsData as any,
        };

        // Close any existing client before creating a new one
        if (clientRef.current) {
          clientRef.current.close();
        }

        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Setting up wallet signer...' });
        const signer = createSigner(authInfo, password);
        pendingSignerRef.current = signer;
        const address = chatAddress || '';
        const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });
        const client = await initializeXmtpClient(signer, address, onProgress);

        // Keep signer in ref for periodic background backups
        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Loading conversations...' });
        dispatch({ type: 'SET_CLIENT', payload: client });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        dispatch({ type: 'SET_INITIALIZING', payload: false });
      } catch (err: any) {
        if (err?.name === 'InstallationLimitError' && err.inboxId) {
          dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
          return;
        }
        pendingSignerRef.current = null;
        console.error('Failed to initialize XMTP:', err);
        releaseTabLock();
        dispatch({
          type: 'SET_INIT_ERROR',
          payload: err.message || 'Failed to initialize chat',
        });
        toast.error('Failed to initialize chat. Please try again.');
      }
    },
    [state.isInitialized, state.isInitializing, loginOption, entityInfo, email, hasPassword, userWalletsData, chatAddress]
  );

  // ---- Complete registration after user revokes installations ----
  const completeRegistration = useCallback(async () => {
    dispatch({ type: 'CLEAR_INSTALLATION_LIMIT' });
    dispatch({ type: 'SET_INITIALIZING', payload: true });
    dispatch({ type: 'SET_INIT_ERROR', payload: null });
    const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });

    try {
      let client: Client;
      const signer = pendingSignerRef.current;
      const address = chatAddress || '';
      if (state.pendingClient) {
        // We have a client (from register() failure) — just retry registration
        client = await registerAfterRevocation(state.pendingClient, onProgress, signer || undefined, address);
      } else {
        // No client (Client.create() failed) — full re-init with signer
        if (!signer) {
          throw new Error('No signer available. Please try enabling chat again.');
        }
        client = await createXmtpClient(signer, address, onProgress);
      }
      // Keep signer in ref for periodic background backups
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Loading conversations...' });
      dispatch({ type: 'SET_CLIENT', payload: client });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      toast.success('Device registered successfully.', { duration: 5000 });
    } catch (err: any) {
      console.error('Registration after revocation failed:', err);
      releaseTabLock();
      dispatch({
        type: 'SET_INIT_ERROR',
        payload: err.message || 'Failed to register after revoking installations',
      });
      toast.error('Registration failed. Please try again.');
    }
  }, [state.pendingClient, chatAddress]);

  // ---- Dismiss installation manager without revoking ----
  const dismissInstallationManager = useCallback(() => {
    if (state.pendingClient) {
      try { state.pendingClient.close(); } catch { /* ignore */ }
    }
    pendingSignerRef.current = null;
    releaseTabLock();
    dispatch({ type: 'CLEAR_INSTALLATION_LIMIT' });
  }, [state.pendingClient]);

  // ---- Revoke installations (uses static Client method with stored signer) ----
  const revokeInstallations = useCallback(
    async (installationBytes: Uint8Array[], revokeAll: boolean) => {
      const client = state.pendingClient;
      const inboxId = state.pendingInboxId || client?.inboxId;
      const signer = pendingSignerRef.current;

      // Try instance method first (works if client was created with a signer)
      if (client) {
        try {
          if (revokeAll) {
            await client.revokeAllOtherInstallations();
          } else {
            await client.revokeInstallations(installationBytes);
          }
          return;
        } catch (instanceErr: any) {
          console.warn('Instance revocation failed, trying static method:', instanceErr.message);
        }
      }

      // Fallback: use static Client.revokeInstallations() with the stored signer
      if (!signer || !inboxId) {
        throw new Error('No signer or inbox ID available for revocation');
      }
      await (Client as any).revokeInstallations(
        signer,
        inboxId,
        installationBytes,
        XMTP_ENV,
      );
    },
    [state.pendingClient, state.pendingInboxId]
  );

  // ---- Auto-initialize: try cached DB first, then full init (no user click needed) ----
  useEffect(() => {
    if (state.isInitialized || !chatAddress || !isLoggedIn) return;
    if (autoInitAttemptedRef.current) return;
    autoInitAttemptedRef.current = true;

    async function tryAutoInit() {
      if (!acquireTabLock()) {
        autoInitAttemptedRef.current = false;
        return;
      }

      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_ERROR', payload: null });
      const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });

      try {
        // Step 1: Try to rebuild from cached OPFS database (no signing needed)
        onProgress('Reconnecting from cached session...');
        const cachedClient = await buildXmtpClient(chatAddress!, onProgress);
        if (cachedClient && await cachedClient.isRegistered()) {
          // Create signer for background backups (if possible without user interaction)
          if (!hasPassword) {
            try {
              const authInfo = {
                loginOption,
                activePublicKey: entityInfo.activePublicKey,
                email,
                indexWallet: entityInfo.indexWallet,
                hasPassword,
                userWalletsData: userWalletsData as any,
              };
              const signer = createSigner(authInfo);
              pendingSignerRef.current = signer;
              console.log('[XMTP AutoInit] Signer created, triggering ensureFirebaseBackup for', chatAddress);
              // Sync local data to Firebase in background
              ensureFirebaseBackup(chatAddress!, signer).catch((err) => {
                console.error('[XMTP AutoInit] Background Firebase backup sync FAILED:', err?.message || err);
              });
            } catch (signerErr: any) {
              console.error('[XMTP AutoInit] Could not create signer for backup:', signerErr?.message || signerErr);
            }
          } else {
            console.log('[XMTP AutoInit] hasPassword=true, skipping backup (signer needs password)');
          }
          onProgress('Loading conversations...');
          dispatch({ type: 'SET_CLIENT', payload: cachedClient });
          dispatch({ type: 'SET_INITIALIZED', payload: true });
          dispatch({ type: 'SET_INITIALIZING', payload: false });
          return;
        }
        cachedClient?.close();

        // Step 2: Cache unavailable — try full init with signer (automatic for custodial users)
        // Skip for password-protected users since we can't sign without their password
        if (hasPassword) {
          releaseTabLock();
          dispatch({ type: 'SET_INITIALIZING', payload: false });
          dispatch({ type: 'SET_INIT_PROGRESS', payload: null });
          return;
        }

        const authInfo = {
          loginOption,
          activePublicKey: entityInfo.activePublicKey,
          email,
          indexWallet: entityInfo.indexWallet,
          hasPassword,
          userWalletsData: userWalletsData as any,
        };

        onProgress('Setting up wallet signer...');
        const signer = createSigner(authInfo);
        pendingSignerRef.current = signer;
        const client = await initializeXmtpClient(signer, chatAddress!, onProgress);
        // Keep signer in ref for periodic background backups
        onProgress('Loading conversations...');
        dispatch({ type: 'SET_CLIENT', payload: client });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        dispatch({ type: 'SET_INITIALIZING', payload: false });
      } catch (err: any) {
        if (err?.name === 'InstallationLimitError' && err.inboxId) {
          dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
          return;
        }
        pendingSignerRef.current = null;
        console.warn('Auto-init failed, user must click Enable Chat:', err.message);
        releaseTabLock();
        dispatch({ type: 'SET_INITIALIZING', payload: false });
        dispatch({ type: 'SET_INIT_PROGRESS', payload: null });
      }
    }

    tryAutoInit();
  }, [chatAddress, isLoggedIn, state.isInitialized, loginOption, entityInfo, email, hasPassword, userWalletsData]);

  // ---- Sync conversations ----
  const syncConversations = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;

    dispatch({ type: 'SET_SYNCING', payload: true });
    try {
      // Network sync with retry — ensures conversations are fetched even on flaky connections
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          await client.conversations.sync();
          break;
        } catch (err: any) {
          console.warn(`Conversation sync attempt ${attempt}/5 failed:`, err);
          if (attempt < 5) {
            await new Promise((r) => setTimeout(r, 2000));
          }
        }
      }
      await client.conversations.syncAll().catch(() => {});

      const allConvos = await client.conversations.list();

      const myInboxId = client.inboxId || '';
      const unreadCounts: Record<string, number> = {};
      let totalUnread = 0;

      const conversations: ConversationData[] = await Promise.all(
        allConvos.map(async (convo: any) => {
          const convoData = await toConversation(convo, client);
          // Load recent messages for preview and unread counting
          try {
            // Sync each conversation to pull latest messages from network
            await convo.sync().catch(() => {});
            const recentMsgs = await convo.messages({ limit: BigInt(50) });
            const lastReadTime = getLastReadTime(convoData.id);

            // Parse all recent messages and sort by time (SDK order is not guaranteed)
            const parsedMsgs = recentMsgs
              .map((m: any) => toMessage(m, myInboxId))
              .filter((m: any): m is ChatMessage => m !== null)
              .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

            // Set latest message for preview (last after sort = newest)
            if (parsedMsgs.length > 0) {
              const latest = parsedMsgs[parsedMsgs.length - 1];
              convoData.lastMessageText =
                typeof latest.content === 'string'
                  ? latest.content.slice(0, 100)
                  : latest.content?.isVoiceNote
                    ? '\uD83C\uDFA4 Voice message'
                    : '[Attachment]';
              convoData.lastMessageTime = latest.sentAt;
              convoData.lastMessageSender = latest.senderAddress;
            }

            // Count unread — if user sent any message after lastReadTime,
            // they were active in this conversation and everything is read
            let unread = 0;
            const userSentAfterLastRead = parsedMsgs.some(
              (m: ChatMessage) => m.isSentByMe && m.sentAt.getTime() > lastReadTime
            );
            if (!userSentAfterLastRead) {
              for (const msg of parsedMsgs) {
                if (!msg.isSentByMe && msg.sentAt.getTime() > lastReadTime) {
                  unread++;
                }
              }
            }

            if (unread > 0) {
              unreadCounts[convoData.id] = unread;
              totalUnread += unread;
            }
          } catch {
            // Not critical — conversation still shows
          }
          return convoData;
        })
      );

      // Merge with cached previews: if the localStorage cache has a newer
      // lastMessageTime for a conversation (e.g. from a stream-delivered or
      // sent message), keep the cached preview instead of the OPFS one.
      // This prevents stale OPFS data from overwriting fresh cache data.
      if (chatAddress) {
        const cachedConvos = getCachedConversations(chatAddress);
        if (cachedConvos) {
          const cachedMap = new Map(cachedConvos.map((c) => [c.id, c]));
          conversations.forEach((convo) => {
            const cached = cachedMap.get(convo.id);
            if (!cached) return;
            const cachedTime = cached.lastMessageTime?.getTime() || 0;
            const networkTime = convo.lastMessageTime?.getTime() || 0;
            if (cachedTime > networkTime) {
              // Cache has a newer preview — keep it
              convo.lastMessageText = cached.lastMessageText;
              convo.lastMessageTime = cached.lastMessageTime;
              convo.lastMessageSender = cached.lastMessageSender;
            }
          });
        }
      }

      // Sort by last message time or created time
      conversations.sort((a, b) => {
        const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
        const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
        return timeB - timeA;
      });

      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });

      // Persist to localStorage for instant loading next time
      if (chatAddress) {
        setCachedConversations(chatAddress, conversations);
      }

      // Set initial unread counts from sync
      if (totalUnread > 0) {
        Object.entries(unreadCounts).forEach(([convId, count]) => {
          for (let i = 0; i < count; i++) {
            dispatch({ type: 'INCREMENT_UNREAD', payload: convId });
          }
        });
      }
    } catch (err) {
      console.error('Error syncing conversations:', err);
    } finally {
      dispatch({ type: 'SET_SYNCING', payload: false });
    }
  }, [chatAddress]);

  // ---- Load messages for a conversation ----
  const loadMessages = useCallback(async (conversationId: string) => {
    const client = clientRef.current;
    if (!client) return;

    // Load cached messages instantly while network sync happens
    const cached = getCachedMessages(conversationId);
    if (cached && cached.length > 0) {
      dispatch({ type: 'SET_MESSAGES', payload: { conversationId, messages: cached } });
    }

    dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: true } });

    try {
      const allConvos = await client.conversations.list();
      const convo = allConvos.find(
        (c: any) => (c.id || c.topic) === conversationId
      );
      if (!convo) return;

      await convo.sync().catch((err: any) =>
        console.warn('Conversation sync failed, loading cached messages:', err)
      );
      const msgs = await convo.messages({ limit: BigInt(MESSAGE_PAGE_SIZE) });

      const myInboxId = client.inboxId || '';
      const networkMessages: ChatMessage[] = msgs
        .map((m: any) => toMessage(m, myInboxId))
        .filter((m: ChatMessage | null): m is ChatMessage => m !== null)
        .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

      // Resolve sender inbox IDs to Ethereum addresses for display
      const uniqueSenderIds = Array.from(new Set(networkMessages.map((m) => m.senderAddress)));
      const addressMap = new Map<string, string>();
      await Promise.all(
        uniqueSenderIds.map(async (inboxId) => {
          const addr = await resolveInboxIdToAddress(client, inboxId);
          addressMap.set(inboxId, addr);
        })
      );
      networkMessages.forEach((m) => {
        m.senderAddress = addressMap.get(m.senderAddress) || m.senderAddress;
      });

      // Merge network messages with cached messages so that any
      // stream-delivered messages in the cache aren't lost if the
      // network sync hasn't caught up yet.
      // Filter out local- placeholder messages from cache (they would
      // duplicate real messages from the network that have different IDs).
      const merged = cached
        ? mergeMessages(
            cached.filter((m) => !m.id.startsWith('local-')),
            networkMessages
          )
        : networkMessages;

      dispatch({ type: 'SET_MESSAGES', payload: { conversationId, messages: merged } });
      dispatch({
        type: 'SET_HAS_MORE_MESSAGES',
        payload: { conversationId, hasMore: msgs.length >= MESSAGE_PAGE_SIZE },
      });

      // Persist merged messages to localStorage
      setCachedMessages(conversationId, merged);

      // Update sidebar preview with the most recent message
      if (merged.length > 0) {
        const latest = merged[merged.length - 1];
        let textPreview: string;
        if (latest.contentType === 'remoteAttachment' && latest.content?.isVoiceNote) {
          textPreview = '\uD83C\uDFA4 Voice message';
        } else if (typeof latest.content === 'string') {
          textPreview = latest.content.slice(0, 100);
        } else {
          textPreview = latest.contentType === 'remoteAttachment' ? '[Attachment]' : '[Attachment]';
        }
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: conversationId,
            text: textPreview,
            time: latest.sentAt,
            sender: latest.senderAddress,
          },
        });
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: false } });
    }
  }, []);

  // ---- Load older messages (pagination) ----
  const loadOlderMessages = useCallback(
    async (conversationId: string) => {
      const client = clientRef.current;
      if (!client) return;

      const existingMessages = state.messages[conversationId] || [];
      if (existingMessages.length === 0) return;

      dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: true } });

      try {
        const allConvos = await client.conversations.list();
        const convo = allConvos.find(
          (c: any) => (c.id || c.topic) === conversationId
        );
        if (!convo) return;

        // Use the oldest message's timestamp as cursor to fetch earlier messages
        const oldestMessage = existingMessages[0];
        const beforeNs = BigInt(oldestMessage.sentAt.getTime()) * BigInt(1_000_000);

        const msgs = await convo.messages({
          limit: BigInt(MESSAGE_PAGE_SIZE),
          sentBeforeNs: beforeNs,
        });

        const myInboxId = client.inboxId || '';
        const existingIds = new Set(existingMessages.map((m) => m.id));
        const olderMsgs = msgs
          .map((m: any) => toMessage(m, myInboxId))
          .filter((m: ChatMessage | null): m is ChatMessage => m !== null)
          .filter((m: ChatMessage) => !existingIds.has(m.id))
          .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

        // Resolve sender addresses
        const uniqueSenderIds = Array.from(new Set(olderMsgs.map((m) => m.senderAddress)));
        const addressMap = new Map<string, string>();
        await Promise.all(
          uniqueSenderIds.map(async (inboxId) => {
            const addr = await resolveInboxIdToAddress(client, inboxId);
            addressMap.set(inboxId, addr);
          })
        );
        olderMsgs.forEach((m) => {
          m.senderAddress = addressMap.get(m.senderAddress) || m.senderAddress;
        });

        if (olderMsgs.length > 0) {
          dispatch({ type: 'PREPEND_MESSAGES', payload: { conversationId, messages: olderMsgs } });
        }
        dispatch({
          type: 'SET_HAS_MORE_MESSAGES',
          payload: { conversationId, hasMore: msgs.length >= MESSAGE_PAGE_SIZE },
        });
      } catch (err) {
        console.error('Error loading older messages:', err);
      } finally {
        dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: false } });
      }
    },
    [state.messages]
  );

  // ---- Send message ----
  const sendMessage = useCallback(
    async (conversationId: string, text: string, attachment?: RemoteAttachmentMetadata) => {
      const client = clientRef.current;
      if (!client || (!text.trim() && !attachment)) return;

      try {
        const allConvos = await client.conversations.list();
        const convo = allConvos.find(
          (c: any) => (c.id || c.topic) === conversationId
        );
        if (!convo) {
          toast.error('Conversation not found');
          return;
        }

        // Sync conversation state before sending to ensure MLS group is current
        await convo.sync();

        if (attachment) {
          // Send attachment metadata as a prefixed JSON string
          const payload = ATTACHMENT_PREFIX + JSON.stringify(attachment);
          await convo.send(payload);
        } else {
          await convo.send(text.trim());
        }

        // Ensure messages are published to the XMTP network
        await convo.publishMessages();

        // Immediately update sidebar preview so conversation order stays current
        const sentTime = new Date();
        let textPreview: string;
        if (attachment?.isVoiceNote) {
          textPreview = '\uD83C\uDFA4 Voice message';
        } else if (attachment) {
          textPreview = '[Attachment]';
        } else {
          textPreview = text.trim().slice(0, 100);
        }
        const senderAddr = client.inboxId || '';
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: conversationId,
            text: textPreview,
            time: sentTime,
            sender: senderAddr,
          },
        });

        // Persist preview to localStorage so it survives refresh
        if (chatAddress) {
          updateConversationCachePreview(chatAddress, conversationId, textPreview, sentTime, senderAddr);
        }

        // Note: sent message will be cached by the stream handler when it
        // arrives back via streamAllMessages (with the real XMTP message ID).
        // We don't cache a local placeholder here to avoid duplicate messages
        // on refresh (local ID ≠ XMTP ID → merge treats them as different).

        // Mark conversation as read — the user is actively participating
        setLastReadTime(conversationId);
      } catch (err: any) {
        console.error('Error sending message:', err);
        toast.error('Failed to send message');
      }
    },
    [chatAddress]
  );

  // ---- Select conversation ----
  const selectConversation = useCallback((conversationId: string | null) => {
    if (conversationId) {
      // Snapshot the last-read time BEFORE marking as read (for new-messages divider)
      lastReadAtRef.current = getLastReadTime(conversationId);
      dispatch({ type: 'CLEAR_UNREAD', payload: conversationId });
      setLastReadTime(conversationId);
    } else {
      lastReadAtRef.current = 0;
    }
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
  }, []);

  // ---- Create DM ----
  const createDm = useCallback(
    async (peerAddress: string): Promise<string | null> => {
      const client = clientRef.current;
      if (!client) return null;

      try {
        // Check if the address is reachable on XMTP before attempting DM
        const reachable = await canMessage(client, [peerAddress]);
        if (!reachable.get(peerAddress.toLowerCase())) {
          toast.error('This address is not registered on XMTP and cannot receive messages yet.');
          return null;
        }

        const identifier = {
          identifier: peerAddress.toLowerCase(),
          identifierKind: "Ethereum" as const,
        };
        const dm = await client.conversations.newDmWithIdentifier(identifier);
        const convoData = { ...(await toConversation(dm, client)), peerAddress: peerAddress.toLowerCase() };
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        return convoData.id;
      } catch (err: any) {
        console.error('Error creating DM:', err);
        toast.error('Failed to start conversation');
        return null;
      }
    },
    []
  );

  // ---- Create Group ----
  const createGroup = useCallback(
    async (memberAddresses: string[], name?: string): Promise<string | null> => {
      const client = clientRef.current;
      if (!client) return null;

      try {
        const identifiers = memberAddresses.map((addr) => ({
          identifier: addr.toLowerCase(),
          identifierKind: "Ethereum" as const,
        }));
        const group = await client.conversations.newGroupWithIdentifiers(
          identifiers,
          { name: name || undefined }
        );
        const convoData = await toConversation(group, client);
        convoData.isGroup = true;
        convoData.groupName = name;
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        return convoData.id;
      } catch (err: any) {
        console.error('Error creating group:', err);
        toast.error('Failed to create group');
        return null;
      }
    },
    []
  );

  // ---- Add members to group ----
  const addGroupMembers = useCallback(
    async (conversationId: string, addresses: string[]) => {
      const client = clientRef.current;
      if (!client) return;

      try {
        const allConvos = await client.conversations.list();
        const convo = allConvos.find((c: any) => (c.id || c.topic) === conversationId);
        if (!convo) throw new Error('Conversation not found');

        await convo.sync();
        const identifiers = addresses.map((addr) => ({
          identifier: addr.toLowerCase(),
          identifierKind: "Ethereum" as const,
        }));
        await (convo as any).addMembersByIdentifiers(identifiers);
        await convo.sync();

        // Refresh member list
        const updated = await toConversation(convo, client);
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: updated.members || [] } });
        toast.success(`Added ${addresses.length} member(s)`);
      } catch (err: any) {
        console.error('Error adding group members:', err);
        toast.error(err.message || 'Failed to add members');
      }
    },
    []
  );

  // ---- Remove member from group ----
  const removeGroupMember = useCallback(
    async (conversationId: string, address: string) => {
      const client = clientRef.current;
      if (!client) return;

      try {
        const allConvos = await client.conversations.list();
        const convo = allConvos.find((c: any) => (c.id || c.topic) === conversationId);
        if (!convo) throw new Error('Conversation not found');

        await convo.sync();
        const identifier = {
          identifier: address.toLowerCase(),
          identifierKind: "Ethereum" as const,
        };
        await (convo as any).removeMembersByIdentifiers([identifier]);
        await convo.sync();

        // Refresh member list
        const updated = await toConversation(convo, client);
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: updated.members || [] } });
        toast.success('Member removed');
      } catch (err: any) {
        console.error('Error removing group member:', err);
        toast.error(err.message || 'Failed to remove member');
      }
    },
    []
  );

  // ---- Start message stream when client is ready ----
  useEffect(() => {
    const client = state.client;
    if (!client || !state.isInitialized) return;

    let cancelled = false;

    async function startStreams() {
      try {
        // Stream all messages
        const msgStream = await client!.conversations.streamAllMessages();
        streamRef.current = msgStream;

        for await (const msg of msgStream) {
          if (cancelled) break;
          const myInboxId = client!.inboxId || '';
          const message = toMessage(msg, myInboxId);

          // Skip XMTP system/internal messages (group membership changes, etc.)
          if (!message) continue;

          // Resolve sender inbox ID to Ethereum address before dispatching/caching
          try {
            const resolved = await resolveInboxIdToAddress(client!, message.senderAddress);
            if (resolved && resolved !== message.senderAddress) {
              message.senderAddress = resolved;
            }
          } catch { /* non-critical — keep inbox ID */ }

          dispatch({
            type: 'APPEND_MESSAGE',
            payload: { conversationId: message.conversationId, message },
          });

          // Append to message cache (with deduplication)
          const existingCache = getCachedMessages(message.conversationId) || [];
          if (!existingCache.some((m) => m.id === message.id)) {
            existingCache.push(message);
            setCachedMessages(message.conversationId, existingCache);
          }

          // Update conversation's last message
          let textPreview: string;
          if (message.contentType === 'remoteAttachment' && message.content?.isVoiceNote) {
            textPreview = '\uD83C\uDFA4 Voice message';
          } else if (typeof message.content === 'string') {
            textPreview = message.content.slice(0, 100);
          } else {
            textPreview = message.contentType === 'remoteAttachment' ? '[Attachment]' : (
              typeof msg.content === 'string' ? msg.content.slice(0, 100) : '[Attachment]'
            );
          }
          dispatch({
            type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
            payload: {
              id: message.conversationId,
              text: textPreview,
              time: message.sentAt,
              sender: message.senderAddress,
            },
          });

          // Update conversation cache with new preview
          if (chatAddress) {
            updateConversationCachePreview(chatAddress, message.conversationId, textPreview, message.sentAt, message.senderAddress);
          }

          // Increment unread if not the active conversation and not sent by me
          // Use ref to always get the current activeConversationId (avoids stale closure)
          if (
            message.conversationId !== activeConvoRef.current &&
            !message.isSentByMe
          ) {
            dispatch({ type: 'INCREMENT_UNREAD', payload: message.conversationId });
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Message stream error:', err);
        }
      }
    }

    async function startConvoStream() {
      try {
        const cStream = await client!.conversations.stream();
        convoStreamRef.current = cStream;

        for await (const convo of cStream) {
          if (cancelled) break;
          const convoData = await toConversation(convo, client!);
          dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Conversation stream error:', err);
        }
      }
    }

    // Load cached conversations instantly so the sidebar appears immediately
    if (chatAddress) {
      const cached = getCachedConversations(chatAddress);
      if (cached && cached.length > 0) {
        dispatch({ type: 'SET_CONVERSATIONS', payload: cached });
      }
    }

    // Sync from network, then start streams.
    // After initial sync, schedule a delayed re-sync to catch conversations
    // that may still be propagating on the network (especially after re-registration).
    syncConversations().then(() => {
      if (!cancelled) {
        startStreams();
        startConvoStream();

        // Delayed re-syncs: XMTP history sync delivers messages asynchronously
        // from the history sync server after a new installation registers.
        // Multiple re-syncs at increasing intervals catch late-arriving history.
        setTimeout(() => {
          if (!cancelled) syncConversations();
        }, 5000);
        setTimeout(() => {
          if (!cancelled) syncConversations();
        }, 15000);
        setTimeout(() => {
          if (!cancelled) syncConversations();
        }, 30000);
      }
    });

    // Periodic background backup: persist OPFS database to Firebase every 5 minutes.
    // Ensures the latest messages are backed up for recovery after key rotation.
    const backupInterval = setInterval(() => {
      const signer = pendingSignerRef.current;
      if (!cancelled && chatAddress && signer) {
        console.log('[XMTP Periodic] Triggering periodic backup for', chatAddress);
        backupXmtpDatabase(chatAddress, signer).catch((err) => {
          console.error('[XMTP Periodic] Periodic backup failed:', err?.message || err);
        });
      } else if (!cancelled) {
        console.log(`[XMTP Periodic] Skipping backup: chatAddress=${chatAddress}, signer=${!!signer}`);
      }
    }, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(backupInterval);
      streamRef.current?.return?.();
      convoStreamRef.current?.return?.();
    };
  }, [state.client, state.isInitialized]);

  // ---- Cleanup on logout ----
  useEffect(() => {
    if (!isLoggedIn && (state.isInitialized || state.pendingClient)) {
      closeXmtpClient(state.client);
      if (state.pendingClient) {
        try { state.pendingClient.close(); } catch { /* ignore */ }
      }
      pendingSignerRef.current = null;
      streamRef.current?.return?.();
      convoStreamRef.current?.return?.();
      autoInitAttemptedRef.current = false;
      dispatch({ type: 'RESET' });
    }
  }, [isLoggedIn]);

  // ---- Context values ----
  const clientContextValue: ChatClientContextValue = {
    isInitialized: state.isInitialized,
    isInitializing: state.isInitializing,
    initError: state.initError,
    initProgress: state.initProgress,
    chatAddress,
    initializeChat,
    showInstallationManager: state.showInstallationManager,
    pendingClient: state.pendingClient,
    pendingInboxId: state.pendingInboxId,
    completeRegistration,
    dismissInstallationManager,
    revokeInstallations,
  };

  const dataContextValue: ChatDataContextValue = {
    conversations: state.conversations,
    activeConversationId: state.activeConversationId,
    messages: state.messages,
    loadingMessages: state.loadingMessages,
    hasMoreMessages: state.hasMoreMessages,
    unreadCounts: state.unreadCounts,
    totalUnread: state.totalUnread,
    searchQuery: state.searchQuery,
    isSyncing: state.isSyncing,
    conversationFilter: state.conversationFilter,
    lastReadAt: lastReadAtRef.current,
    dispatch,
    sendMessage,
    loadMessages,
    loadOlderMessages,
    selectConversation,
    createDm,
    createGroup,
    addGroupMembers,
    removeGroupMember,
    syncConversations,
  };

  return (
    <ChatClientContext.Provider value={clientContextValue}>
      <ChatDataContext.Provider value={dataContextValue}>
        {children}
      </ChatDataContext.Provider>
    </ChatClientContext.Provider>
  );
}

export const useChatClient = () => useContext(ChatClientContext);
export const useChatData = () => useContext(ChatDataContext);
